import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { SelectOption } from '@/shared/types';
import { FormSelect, FormTextInput, Informer, StepControlPanel } from '@/shared/ui';

import { ServicePaymentDataProps } from '@/modules/pay/pages/services/ServicePaymentWithoutApplication/model/types';

import AuthContext from '@/contexts/Auth/AuthContext';

import { getOrgNpuSelect, getServicesForPaymentSelect } from '@/api/apiService';
import { PaymentServiceResponseOption, DocumentTypeCode } from '@/api/types';

import api from '@/api';

import { AWARD_WEAPON_PERMIT_CODE, WEAPON_CONTROL_DEPT_ID } from '../../lib/constants';
import { ServicePaymentMergedSchema } from '../../model/schemas';

export interface ServicePaymentDataOwnProps {
  generatePaymentDetailsInfo?: (
    formData: ServicePaymentMergedSchema,
  ) => ReturnType<typeof api.pay.services.paymentInfo>;
  checkDefraymentServiceAvailability?: (formData: ServicePaymentDataProps) => Promise<boolean>;
  isPymentServiceAvailable?: boolean;
}

export const ServicePaymentData: React.FC<StepProps<ServicePaymentDataOwnProps>> = ({
  next,
  generatePaymentDetailsInfo,
  checkDefraymentServiceAvailability,
  isPymentServiceAvailable = false,
}) => {
  const { userData } = useContext(AuthContext);
  const { control, getValues, watch, setValue } = useFormContext<ServicePaymentMergedSchema>();

  const { fullName, rnokpp } = getValues();

  const [servicesOptions, setServicesOptions] = useState<Nullable<PaymentServiceResponseOption[]>>([]);
  const [optionsOrgNpu, setOptionsOrgNpu] = useState<Array<SelectOption & { isPayable: boolean }>>([]);

  useEffect(() => {
    if (isEmpty(userData)) return;

    isEmpty(fullName) &&
      setValue('fullName', userData.name_last + ' ' + userData.name_first + ' ' + userData.name_middle);
    isEmpty(rnokpp) && setValue(DocumentTypeCode.RNOKPP, userData.drfo_code);
  }, [getValues, setValue, userData]);

  const defraymentService = watch('defraymentService');
  const defraymentSubService = watch('defraymentSubService');
  const orgNpu = watch('orgNpu');

  const isAwardWeaponPermitSelected =
    defraymentSubService?.data?.find((item) => item?.id === defraymentSubService?.id)?.code ===
    AWARD_WEAPON_PERMIT_CODE;

  const defraymentSubServiceOptions =
    defraymentService?.data.map((item) => ({
      id: item.id,
      label: item.sub_name,
      data: defraymentService.data,
    })) ?? [];

  useEffect(() => {
    if (!defraymentService) return;
    if (defraymentSubServiceOptions.length === 1 && defraymentSubService === undefined) return;

    if (defraymentSubServiceOptions.length > 1 && defraymentSubService === undefined) {
      setValue('defraymentSubService', null);
    }

    if (defraymentSubServiceOptions.length === 1) {
      setValue('defraymentSubService', undefined);
    }
  }, [defraymentService, defraymentSubServiceOptions]);

  useEffect(() => {
    if (!defraymentService || defraymentSubServiceOptions.some(({ id }) => id === defraymentSubService?.id)) return;
    setValue('defraymentSubService', undefined);
  }, [defraymentService]);

  useEffect(() => {
    if (!isAwardWeaponPermitSelected) return;

    const preSelectedWeaponControlOrgNpu = optionsOrgNpu.find((item) => item.id === WEAPON_CONTROL_DEPT_ID) ?? null;
    if (preSelectedWeaponControlOrgNpu) {
      // @ts-ignore
      setValue('orgNpu', preSelectedWeaponControlOrgNpu);
      return;
    }
  }, [isAwardWeaponPermitSelected, optionsOrgNpu]);

  useEffect(() => {
    getServicesForPaymentSelect(setServicesOptions);
    getOrgNpuSelect(setOptionsOrgNpu);
  }, []);

  const handleNextStep = async (): Promise<void> => {
    if (typeof generatePaymentDetailsInfo !== 'function') return;
    const freshFormData = getValues();

    try {
      const { data } = await generatePaymentDetailsInfo(freshFormData);
      setValue('paymentInfo', data);
    } finally {
      const isServiceAvailable = await checkDefraymentServiceAvailability?.(freshFormData);
      if (isServiceAvailable) {
        next();
      }
    }
  };

  const showSubServiceSelect = defraymentService?.data && defraymentService?.data?.length > 1;
  const isOrgNpuNotPayable: boolean = Boolean(orgNpu && !orgNpu?.isPayable);

  return (
    <>
      <FormBlock title="Вкажіть дані для оплати">
        <FormSelect
          label="Назва послуги для оплати"
          control={control}
          name="defraymentService"
          options={servicesOptions as any}
        />
        {showSubServiceSelect && (
          <FormSelect
            label="Підтип послуги"
            control={control}
            name="defraymentSubService"
            options={defraymentSubServiceOptions}
          />
        )}

        <FormSelect
          label="Виберіть орган НПУ, де плануєте отримати послугу"
          control={control}
          name="orgNpu"
          disabled={isAwardWeaponPermitSelected}
          options={optionsOrgNpu}
        />

        {isOrgNpuNotPayable && (
          <Informer
            icon={{
              name: 'attention',
              color: 'red',
            }}
            header={{
              text: 'Зверніть увагу!',
              color: 'red',
            }}
            text={{
              color: 'black',
              text: 'Для цього органу НПУ тимчасово відсутня можливість оплатити послугу онлайн.',
            }}
          />
        )}

        <FormTextInput
          name="fullName"
          control={control}
          label="Вкажіть ПІБ особи-платника або особи, від імені якої сплачується послуга"
          required
        />

        <FormTextInput
          name="rnokpp"
          control={control}
          label="Вкажіть ідентифікаційний код (РНОКПП) або в разі його відсутності номер паспорта, ID картки або закордонного паспорта"
          required
        />
      </FormBlock>

      {!isPymentServiceAvailable && (
        <div>
          <Informer
            bgColor="sand"
            icon={{ name: 'info', color: 'red' }}
            header={{ text: 'Помилка', color: 'red' }}
            text={{
              color: 'black',
              text: 'Для вибраної послуги тимчасово відсутня можливість оплатити послугу онлайн. Спробуйте оплатити пізніше.',
            }}
          />
        </div>
      )}

      <StepControlPanel next={handleNextStep} />
    </>
  );
};
